import {
  WarsEvents,
  UIContextEvent,
  ShopPurchaseEvent,
  ShopDataEvent,
  PurchaseItemLoadingEvent,
  UpdateCurrentPlayerEvent,
  PlayPreloadAudioEvent,
} from "@playroomkit/pr-wars/events";
import { createContext, useContext, useEffect, useState } from "react";
import { AuthPlatform, useAuth } from "./AuthProvider";
import { useManager } from "./WarsContext";
import {
  PlayerData,
  ShopItemType,
  StoreSkuItem,
  UIContextType,
} from "@playroomkit/pr-wars-data";
import View from "@components/View";
import CircularLoader from "@components/CircularLoader";
import Button from "@components/Button";
import { ItemImage } from "@components/Shop/ShopComponents";
import ScalableWrapper from "@components/ScalableWrapper";

interface ShopContextValue {
  enableShop: boolean;
  showShop: boolean;
  shopItems: StoreSkuItem[];
  filteredShopItems: StoreSkuItem[];
  selectedCategory: ShopItemType | null;
  player: PlayerData | null;
  isEquipping: string | null;
  setEnableShop: (show: boolean) => void;
  toggleShopVisibility: (show: boolean) => void;
  handleClick: (sku_id: string) => void;
  onCategoryChange: (category: ShopItemType | null) => void;
  showPrice: (price: string | number) => string;
  sortAllSKUItems: (items: StoreSkuItem[]) => StoreSkuItem[];
  handleSubscribe: () => Promise<void>;
}

const ShopContext = createContext<ShopContextValue | undefined>(undefined);

export const ShopProvider = ({ children }: React.PropsWithChildren) => {
  const [enableShop, setEnableShop] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [shopItems, setShopItems] = useState<StoreSkuItem[]>([]);
  const [filteredShopItems, setFilteredShopItems] = useState<StoreSkuItem[]>(
    []
  );
  const [selectedCategory, setSelectedCategory] = useState<ShopItemType | null>(
    null
  );
  const [showShopLoading, setShowShopLoading] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState<{
    sku_id: string;
  } | null>(null);
  const [isEquipping, setIsEquipping] = useState<string | null>(null);

  const { userInfo, authPlatform } = useAuth();
  const manager = useManager();
  const player = manager.getMyPlayer();

  const handlePurchase = async (sku_id: string) => {
    // Prevent click if logged in via discord on web
    if (authPlatform === AuthPlatform.DISCORD) return;

    if (sku_id) {
      document.dispatchEvent(
        new ShopPurchaseEvent({
          sku_id,
          status: "start",
          user: {
            id: userInfo?.user?.id ?? "",
            name: userInfo?.user?.username ?? "",
          },
        })
      );
    }
  };

  const handleEquip = (sku: StoreSkuItem) => {
    if (!player || !sku || (!sku.purchased && !player.allAccess)) return;

    const type = sku.metadata?.type;
    const id = sku.metadata?.id ?? "default";

    switch (type) {
      case ShopItemType.PODIUM:
        setIsEquipping(id);
        document.dispatchEvent(
          new UpdateCurrentPlayerEvent(
            player.displayName ?? "",
            player.description ?? "",
            player.profileImageUrl ?? null,
            player.avatarId ?? null,
            id
          )
        );
        setTimeout(() => setIsEquipping(null), 2000);
        break;

      // TODO: Add avatar equip logic
      case ShopItemType.AVATAR:
        break;

      default:
        break;
    }
  };

  const handleSubscribe = async () => {
    const subscription = shopItems.find((item) => item.metadata?.type === ShopItemType.VIP_PASS);
    
    if (subscription) {
      await handlePurchase(subscription?.sku_id ?? "");
    }
    else {
      console.warn("No subscription found");
    }

  }

  const handleClick = (sku_id: string) => {
    const sku = shopItems.find((item) => item.sku_id === sku_id);
    if (sku && (sku?.purchased || player?.allAccess)) {
      handleEquip(sku);
    } else {
      handlePurchase(sku_id);
    }
  };

  const onCategoryChange = (category: ShopItemType | null) => {
    const filteredShopItems = filterShopItems(category, shopItems);
    document.dispatchEvent(new PlayPreloadAudioEvent("sfx", "shop-trigger-sfx", 1, false));
    setFilteredShopItems(filteredShopItems);
    setSelectedCategory(category);
  };

  const toggleShopVisibility = (show: boolean) => {
    if (showShopLoading || purchaseSuccess) return;
    setShowShop(show);
  };

  // Todo: Add order column to sort the SKUs
  const sortAllSKUItems = (items: StoreSkuItem[]) => {
    const subscriptions: StoreSkuItem[] = [],
      avatars: StoreSkuItem[] = [],
      bots: StoreSkuItem[] = [],
      gameModes: StoreSkuItem[] = [],
      podiums: StoreSkuItem[] = [];

    items.forEach((item) => {
      const shopItemType = item.metadata?.type;

      switch (shopItemType) {
        case ShopItemType.VIP_PASS:
          subscriptions.push(item);
          break;
        case ShopItemType.AVATAR:
          avatars.push(item);
          break;
        case ShopItemType.BOT:
          bots.push(item);
          break;
        case ShopItemType.GAME_MODE:
          gameModes.push(item);
          break;
        case ShopItemType.PODIUM:
          podiums.push(item);
          break;
        default:
          break;
      }
    });

    return [...subscriptions, ...gameModes, ...bots, ...podiums, ...avatars];
  };

  const filterShopItems = (
    category: ShopItemType | null,
    items: StoreSkuItem[]
  ) => {
    if (!category) {
      return items.filter((item) =>
        [ShopItemType.BUNDLE, ShopItemType.VIP_PASS].includes(
          item.metadata?.type as ShopItemType
        )
      );
    } else {
      return items.filter((item) => item.metadata?.type === category);
    }
  };

  const handleUIContextEvent = async (event: Event) => {
    const e = event as UIContextEvent;
    const display = e.hasContext(UIContextType.SHOP);

    if (display) {
      setShowShop(display);

      // Send analytic event
      const analytics = await import("../analytics/analytics.js");

      await analytics.track("shop-open", {
        event: "shop-open",
        logType: "shop-open",
        t: Date.now(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        localTime: new Date().toLocaleString(),
      });
    }
  };

  const showPrice = (price: string | number) => {
    if (typeof price === "string" && price.startsWith("$")) {
      return price;
    } else {
      return `$${price}`;
    }
  };

  const equipPodium = (podiumId: string) => {
    const player = manager.getMyPlayer();
    if (!player) return;

    document.dispatchEvent(
      new UpdateCurrentPlayerEvent(
        player.displayName ?? "",
        player.description ?? "",
        player.profileImageUrl ?? null,
        player.avatarId ?? null,
        podiumId
      )
    );
    setPurchaseSuccess(null);
  };

  // Get Shop data
  useEffect(() => {
    const handleShopDataEvent = (event: Event) => {
      const e = event as ShopDataEvent;
      const items = e.data;
      setShopItems(items);
      const filteredShopItems = filterShopItems(selectedCategory, items);
      setFilteredShopItems(filteredShopItems);
    };

    const handlePurchaseItemLoadingEvent = (event: Event) => {
      const e = event as PurchaseItemLoadingEvent;

      setShowShopLoading(e.show);
    };

    const handlePurchaseItemSuccessEvent = (event: Event) => {
      const e = event as ShopPurchaseEvent;
      const { status, sku_id } = e.detail;
      if (status === "success") {
        setShowShopLoading(false);
        setPurchaseSuccess({ sku_id });
      }
    };

    document.addEventListener(WarsEvents.ShopDataEvent, handleShopDataEvent);
    document.addEventListener(WarsEvents.UIContextEvent, handleUIContextEvent);
    document.addEventListener(
      WarsEvents.PurchaseItemLoadingEvent,
      handlePurchaseItemLoadingEvent
    );
    document.addEventListener(
      WarsEvents.ShopPurchaseEvent,
      handlePurchaseItemSuccessEvent
    );

    return () => {
      document.removeEventListener(
        WarsEvents.ShopDataEvent,
        handleShopDataEvent
      );
      document.removeEventListener(
        WarsEvents.UIContextEvent,
        handleUIContextEvent
      );
      document.removeEventListener(
        WarsEvents.PurchaseItemLoadingEvent,
        handlePurchaseItemLoadingEvent
      );
      document.removeEventListener(
        WarsEvents.ShopPurchaseEvent,
        handlePurchaseItemSuccessEvent
      );
    };
  }, [selectedCategory]);

  return (
    <ShopContext.Provider
      value={{
        enableShop,
        showShop,
        shopItems,
        filteredShopItems,
        selectedCategory,
        player,
        isEquipping,
        setEnableShop,
        toggleShopVisibility,
        handleClick,
        onCategoryChange,
        showPrice,
        sortAllSKUItems,
        handleSubscribe
      }}
    >
      {children}
      {showShopLoading && (
        <View
          enableEventCapturing
          className="absolute top-0 left-0 w-screen h-dvh bg-black/80 font-[Slackey] text-2xl text-white flex items-center justify-center z-[9999999]"
        >
          <CircularLoader className="mr-3 -ml-1 size-5 animate-spin text-white" />
          <p>Purchasing</p>
        </View>
      )}
      {purchaseSuccess && (
        <View
          enableEventCapturing
          className="absolute top-0 left-0 w-screen h-dvh bg-black/80 font-[Slackey] text-2xl text-white flex items-center justify-center z-[9999999]"
        >
          <ScalableWrapper maintainAspectRatioBy='height'> 
            <View className="flex flex-col items-center justify-center gap-8 relative size-full">
              {/* Rotating Background Image */}
              <View className="absolute inset-0 flex items-center justify-center">
                <img
                  src="/img/Shop/PurchaseSuccessBg.webp"
                  alt="Purchase Success Background"
                  className="size-full object-contain"
                />
              </View>

              {/* Content Overlay */}
              <View className="relative z-10 flex flex-col items-center justify-center gap-8">
                {/* Purchase Complete Text */}
                <View className="text-center">
                  <h2 className="text-4xl font-bold mb-2 text-shadow">
                    Purchase Complete
                  </h2>
                </View>

                {(() => {
                  const purchasedItem = shopItems.find(
                    (item) => item.sku_id === purchaseSuccess.sku_id
                  );
                  return purchasedItem?.metadata?.image ? (
                    <View className="relative" enableEventCapturing>
                      <ItemImage
                        src={purchasedItem.metadata.image}
                        alt="SKU Image"
                        className="size-96 object-contain"
                      />
                      {/* Equip Button for Podiums */}
                      {purchasedItem?.metadata?.type === ShopItemType.PODIUM && (
                        <div
                          className="absolute bottom-8 text-4xl text-shadow z-10 w-full text-white text-center"
                          onClick={() =>
                            equipPodium(purchasedItem.metadata?.id ?? "")
                          }
                        >
                          Equip
                        </div>
                      )}
                    </View>
                  ) : null;
                })()}

                {/* Great Button */}
                <View
                  onClick={() => setPurchaseSuccess(null)}
                  enableEventCapturing
                >
                  <div className="rounded-xl p-1 inline-block relative bg-[#FF8000] shadow-[0_2px_0_0_#000]">
                    <Button className="font-[Slackey] w-60 border-none outline-none text-base rounded-lg px-4 py-2 flex items-center justify-center transition-all whitespace-nowrap relative z-10 text-black bg-[#FFB800]">
                      Great!
                    </Button>
                  </div>
                </View>
              </View>
            </View>
          </ScalableWrapper>
        </View>
      )}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error("useShop must be used within an ShopProvider");
  }
  return context;
};
